import { CssBaseline, ThemeProvider } from "@mui/material";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import GlobalState from "./components/GlobalState";
import Loading from "./components/Loading";
import PageTransition from "./components/PageTransition";
import { AlertProvider } from "./contexts/AlertContext";
import { AlternativeUserProvider } from "./contexts/AlternativeUserContext";
import { ThemeModeProvider, useThemeMode } from "./contexts/ThemeContext";
import i18n from "./i18n/config";
import SettingPage from "./pages/setting/Page";
import { createAppTheme } from "./theme/theme";

// Lazy load all pages for code splitting
const AuthPage = lazy(() => import("./pages/AuthPage"));
const HomePage = lazy(() => import("./pages/HomePage"));
const DashboardPage = lazy(() => import("./pages/dashboard/Page"));
const ContributionForm = lazy(
  () => import("./pages/guest-contributions/ContributionForm"),
);
const GuestDetailPage = lazy(
  () => import("./pages/guest-contributions/Detail"),
);
const GuestContributionSearchPage = lazy(
  () => import("./pages/guest-contributions/Filter"),
);
const GuestContributionPage = lazy(
  () => import("./pages/guest-contributions/Page"),
);
const RSVPFilter = lazy(() => import("./pages/rsvp/Filter"));
const RSVPDetail = lazy(() => import("./pages/rsvp/RSVPSummary"));
const RSVPPage = lazy(() => import("./pages/rsvp/RSVP"));
const ManageGuestAddPage = lazy(
  () => import("./pages/manage-guest/AddGuestPage"),
);
const BulkPrintPage = lazy(() => import("./pages/manage-guest/BulkPrintPage"));
const GuestSearchPage = lazy(() => import("./pages/manage-guest/Filter"));
const ManageGuestPage = lazy(() => import("./pages/manage-guest/Page"));
const ManageTagsPage = lazy(
  () => import("./pages/manage-guest/ManageTagsPage"),
);
const PrintQRCodePage = lazy(() => import("./pages/manage-guest/PrintQRCode"));
const ReportPage = lazy(() => import("./pages/report/Page"));
const ReportGuestPage = lazy(() => import("./pages/report/ReportGuestPage"));
const ReportGuestDetailPage = lazy(
  () => import("./pages/report/ReportGuestDetailPage"),
);
const ReportRsvpPage = lazy(() => import("./pages/report/ReportRsvpPage"));
const ReportContributionPage = lazy(
  () => import("./pages/report/ReportContributionPage"),
);
const UserProfilePage = lazy(() => import("./pages/user-profile/Page"));
const CurrencyPage = lazy(() => import("./pages/setting/currency/Page"));
const ExchangeRatePage = lazy(
  () => import("./pages/setting/exchange-rate/Page"),
);
const GuestInviteMessagePage = lazy(
  () => import("./pages/setting/guest-invite/Page"),
);
const InvitationTemplatePage = lazy(
  () => import("./pages/setting/invitation-template/Page"),
);
const LinkPreviewTitlePage = lazy(
  () => import("./pages/setting/link-preview/Page"),
);
const ThankYouMessagePage = lazy(
  () => import("./pages/setting/thank-you/Page"),
);
const ContactUsPage = lazy(() => import("./pages/contact-us/Page"));
const UserGuidePage = lazy(() => import("./pages/user-guide/Page"));

function AppContent() {
  const { mode } = useThemeMode();
  const [language, setLanguage] = useState(i18n.language || "en");

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
      document.documentElement.setAttribute("data-theme", mode);
    }
    const handleLanguageChange = (lng: string) => {
      setLanguage(lng);
      if (typeof document !== "undefined") {
        document.documentElement.lang = lng;
      }
    };
    i18n.on("languageChanged", handleLanguageChange);
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [language, mode]);

  const theme = useMemo(() => createAppTheme(mode, language), [mode, language]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <AlternativeUserProvider>
        <AlertProvider>
          <BrowserRouter>
            <GlobalState>
              <Suspense fallback={<Loading open />}>
                <PageTransition>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/auth" element={<AuthPage />} />
                    <Route
                      path="/guest-contributions"
                      element={<GuestContributionPage />}
                    />
                    <Route
                      path="/guest-contributions/search"
                      element={<GuestContributionSearchPage />}
                    />
                    <Route
                      path="/guest-contributions/add"
                      element={<ContributionForm />}
                    />
                    <Route
                      path="/guest-contributions/edit/:id"
                      element={<ContributionForm />}
                    />
                    <Route
                      path="/guest-contributions/detail/:id"
                      element={<GuestDetailPage />}
                    />
                    <Route path="/manage-guest" element={<ManageGuestPage />} />
                    <Route path="/user-profile" element={<UserProfilePage />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/report" element={<ReportPage />} />
                    <Route path="/report/guest" element={<ReportGuestPage />} />
                    <Route
                      path="/report/guest/detail/:id"
                      element={<ReportGuestDetailPage />}
                    />
                    <Route path="/report/rsvp" element={<ReportRsvpPage />} />
                    <Route
                      path="/report/contribution"
                      element={<ReportContributionPage />}
                    />
                    <Route
                      path="/manage-guest/search"
                      element={<GuestSearchPage />}
                    />
                    <Route
                      path="/manage-guest/print/:id"
                      element={<PrintQRCodePage />}
                    />
                    <Route
                      path="/manage-guest/print-bulk"
                      element={<BulkPrintPage />}
                    />
                    <Route
                      path="/manage-guest/add"
                      element={<ManageGuestAddPage />}
                    />
                    <Route
                      path="/manage-guest/edit/:id"
                      element={<ManageGuestAddPage />}
                    />
                    <Route
                      path="/manage-guest/tags"
                      element={<ManageTagsPage />}
                    />
                    <Route path="/rsvp" element={<RSVPPage />} />
                    <Route path="/rsvp/search" element={<RSVPFilter />} />
                    <Route path="/rsvp/:id" element={<RSVPDetail />} />
                    <Route path="/setting" element={<SettingPage />} />
                    <Route
                      path="/setting/currency"
                      element={<CurrencyPage />}
                    />
                    <Route
                      path="/setting/exchange-rate"
                      element={<ExchangeRatePage />}
                    />
                    <Route
                      path="/setting/guest-invite"
                      element={<GuestInviteMessagePage />}
                    />
                    <Route
                      path="/setting/invitation-template"
                      element={<InvitationTemplatePage />}
                    />
                    <Route
                      path="/setting/invitation-templates"
                      element={<InvitationTemplatePage />}
                    />
                    <Route
                      path="/setting/messages"
                      element={<GuestInviteMessagePage />}
                    />
                    <Route
                      path="/setting/link-preview"
                      element={<LinkPreviewTitlePage />}
                    />
                    <Route
                      path="/setting/seo"
                      element={<LinkPreviewTitlePage />}
                    />
                    <Route
                      path="/setting/thank-you"
                      element={<ThankYouMessagePage />}
                    />
                    <Route path="/contact-us" element={<ContactUsPage />} />
                    <Route path="/feedback" element={<ContactUsPage />} />
                    <Route path="/user-guide" element={<UserGuidePage />} />
                  </Routes>
                </PageTransition>
              </Suspense>
            </GlobalState>
          </BrowserRouter>
          <svg width="0" height="0">
            <filter
              id="mini-liquid-lens"
              x="-50%"
              y="-50%"
              width="200%"
              height="200%"
            >
              <feImage
                x="0"
                y="0"
                result="normalMap"
                xlinkHref="data:image/svg+xml;utf8,
      <svg xmlns='http://www.w3.org/2000/svg' width='300' height='300'>
        <radialGradient id='invmap' cx='50%' cy='50%' r='75%'>
          <stop offset='0%' stop-color='rgb(128,128,255)'/>
          <stop offset='90%' stop-color='rgb(255,255,255)'/>
        </radialGradient>
        <rect width='100%' height='100%' fill='url(#invmap)'/>
      </svg>"
              />
              <feDisplacementMap
                in="SourceGraphic"
                in2="normalMap"
                scale="-252"
                xChannelSelector="R"
                yChannelSelector="G"
                result="displaced"
              />
              <feMerge>
                <feMergeNode in="displaced" />
              </feMerge>
            </filter>
          </svg>
        </AlertProvider>
      </AlternativeUserProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ThemeModeProvider>
      <AppContent />
    </ThemeModeProvider>
  );
}

export default App;
